import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';

type Topic = {
  id: string;
  title: string;
  placeholder?: boolean;
  content: React.ReactNode;
};

const topics: Topic[] = [
  {
    id: 'getting-started',
    title: 'Getting started',
    content: (
      <>
        <p>Go to <strong>theemailhelper.com</strong> and click <strong>Sign in with Google</strong> or <strong>Sign in with Microsoft</strong>. Your account is created automatically on first sign-in — there is no separate registration step.</p>
        <p>All new accounts start with a <strong>7-day free trial</strong>. You do not need to enter payment details to begin — payment is only collected if you choose to continue after the trial ends.</p>
        <p>After signing in, the onboarding wizard walks you through six steps: connecting Gmail, previewing detected subscriptions and invoices, configuring smart replies, and running your initial cleanup. You can skip optional steps and return to them later from Settings.</p>
        <p>Once connected, click <strong>Scan inbox</strong> on the dashboard to analyse your emails. The The Email Helper also runs a background sync every 15 minutes to pick up new mail automatically.</p>
      </>
    ),
  },
  {
    id: 'subscription-management',
    title: 'Subscription management',
    content: (
      <>
        <p>The Email Helper detects subscription and newsletter senders automatically using a combination of signals: the List-Unsubscribe email header, known newsletter domains, subject line patterns, and sender frequency.</p>
        <p>From the <strong>Subscriptions</strong> page you can:</p>
        <ul>
          <li><strong>Archive</strong> — moves all emails from a sender out of your inbox in Gmail. They remain in All Mail, are not deleted, and are fully searchable.</li>
          <li><strong>Label</strong> — applies the <code>the-email-helper</code> label to all emails from a sender so they're easy to find later. Labelling does not move or archive emails.</li>
        </ul>
        <p>The Email Helper only acts on senders you explicitly archive or label. Nothing is changed automatically without your instruction.</p>
        <p>Starter plan supports 1 inbox; Pro supports up to 3 inboxes.</p>
      </>
    ),
  },
  {
    id: 'invoice-archiving',
    title: 'Invoice archiving',
    content: (
      <>
        <p className="help-placeholder-notice">Available on Pro and Enterprise plans.</p>
        <p>Invoice archiving automatically detects emails containing invoices, receipts, and order confirmations, and organises them separately from the rest of your inbox. Detected types include invoices and bills, order confirmations, payment confirmations, and subscription renewal receipts.</p>
        <p>You can configure whether detected invoice emails are labelled or archived in Settings.</p>
        <p>PDF attachment extraction and cloud storage integration (Google Drive, OneDrive) are coming in a future update.</p>
        <p>To access invoice archiving, upgrade to Pro from <strong>Settings → Billing</strong>.</p>
      </>
    ),
  },
  {
    id: 'smart-replies',
    title: 'Smart replies',
    content: (
      <>
        <p>Smart replies allow The Email Helper to draft responses to emails on your behalf, written in a clear professional style. You review and send the draft — nothing is sent without your approval.</p>
        <p>Go to <strong>Auto-Responses</strong> in the sidebar to enable draft replies for important senders, follow-up chains, or both. Draft replies appear in your Gmail drafts folder.</p>
        <p className="help-placeholder-notice">Coming soon: meeting scheduling with calendar availability, voice style learning, and custom reply rules.</p>
        <p>Smart replies are available on Starter, Pro, and Enterprise plans including during the free trial.</p>
      </>
    ),
  },
  {
    id: 'account-tracker',
    title: 'Account tracker',
    placeholder: true,
    content: (
      <>
        <p className="help-placeholder-notice">This feature is coming in a future update.</p>
        <p>The accounts tracker will show every website and service you've signed up for, detected from welcome emails, notifications, and purchase receipts. You'll be able to see when you first registered, when you last received an email from that service, and mark accounts as inactive or deleted.</p>
        <p>The tracker only analyses emails already in your inbox — it does not log into or communicate with any external websites.</p>
      </>
    ),
  },
  {
    id: 'billing',
    title: 'Billing and plans',
    content: (
      <>
        <p>The Email Helper offers three plans:</p>
        <ul>
          <li><strong>Starter</strong> — $25/mo (or $18.75/mo billed annually). 1 inbox, subscription cleanup, smart replies, and meeting scheduling.</li>
          <li><strong>Pro</strong> — $45/mo (or $33.75/mo billed annually). Up to 3 inboxes, everything in Starter plus invoice archiving, critical sender prioritisation, advanced dashboard, and more.</li>
          <li><strong>Enterprise</strong> — custom pricing. Unlimited inboxes, smarter AI, SSO, priority support, and live onboarding. <a href="mailto:hello@theemailhelper.com">Contact us</a>.</li>
        </ul>
        <p>All new Starter and Pro accounts include a <strong>7-day free trial</strong> with no credit card required. Payment is accepted by card (Stripe) or direct debit (GoCardless, UK accounts).</p>
        <p>When your trial ends, access is paused until you add a payment method. Your data is retained — nothing is deleted. You can resume at any time from the pricing page.</p>
        <p>To manage your plan, go to <strong>Settings → Billing → Manage subscription</strong>.</p>
      </>
    ),
  },
  {
    id: 'microsoft-outlook',
    title: 'Connecting Microsoft / Outlook',
    content: (
      <>
        <p>Microsoft account and Outlook support is on the roadmap. Currently only Gmail is supported.</p>
        <p>If you use Outlook, Microsoft 365, or Hotmail, sign up to be notified when Outlook support launches — we'll send a notification to the email address you registered with.</p>
      </>
    ),
  },
  {
    id: 'account-settings',
    title: 'Account and settings',
    content: (
      <>
        <p>Go to <strong>Settings</strong> in the left sidebar to manage your account. From there you can:</p>
        <ul>
          <li>View and manage connected inboxes under <strong>Connected accounts</strong></li>
          <li>Manage your subscription and billing under <strong>Billing</strong></li>
          <li>Update your profile, locale, and time zone</li>
        </ul>
        <p>To disconnect Gmail, go to <strong>Settings → Connected accounts</strong> and click <strong>Disconnect</strong>. Previously archived or labelled emails in Gmail are not affected.</p>
        <p>To delete your account and all stored data, email <a href="mailto:hello@theemailhelper.com">hello@theemailhelper.com</a>. We'll process the request within 30 days.</p>
        <p>The Email Helper stores: sender address, subject line, date, and classification labels so we can show your subscription list; your Gmail OAuth token (encrypted) to act on your behalf; and your billing status. Full email bodies and attachments are not stored.</p>
      </>
    ),
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    content: (
      <>
        <dl className="help-dl">
          <dt>"Reconnect Gmail" prompt</dt>
          <dd>Your Gmail token may have expired or been revoked. Go to <strong>Settings → Connected accounts</strong> and click <strong>Reconnect</strong> to sign in again and restore access.</dd>

          <dt>Connected Gmail with old permissions</dt>
          <dd>An earlier version of The Email Helper used read-only Gmail access. The current version requires <code>gmail.modify</code> to archive and apply labels. If you see a reconnect prompt, follow it to grant the updated permissions.</dd>

          <dt>No subscriptions showing after scan</dt>
          <dd>The first scan can take several minutes for large inboxes. Wait for the progress indicator to finish. If your inbox has few emails matching subscription patterns, new ones will be detected as more mail arrives via the 15-minute background sync.</dd>

          <dt>Scan seems stuck</dt>
          <dd>Refresh the page and check the Gmail status on the dashboard. If still stuck after 10 minutes, try clicking <strong>Scan inbox</strong> again. If the issue persists, contact support.</dd>

          <dt>Archive or label not taking effect in Gmail</dt>
          <dd>Check that your Gmail connection is active in <strong>Settings → Connected accounts</strong>. If it shows as not connected, click <strong>Reconnect</strong> and sign in again.</dd>

          <dt>Trial expired — can't access the app</dt>
          <dd>When your trial ends, access is paused until you start a paid plan. Go to the pricing page, select a plan, and enter your payment details. Your data is retained and access resumes within seconds of payment.</dd>

          <dt>Paid but app still shows trial expired</dt>
          <dd>After payment the app can take up to 30 seconds to activate. Refresh the page. If it still shows expired after a minute, email <a href="mailto:hello@theemailhelper.com">hello@theemailhelper.com</a> with your account email address.</dd>
        </dl>
      </>
    ),
  },
];

export default function HelpPage() {
  const [activeId, setActiveId] = useState(topics[0].id);
  const active = topics.find(t => t.id === activeId) ?? topics[0];

  return (
    <div className="landing">
      <PublicHeader />

      <main className="help-page">
        <div className="help-sidebar">
          <p className="help-sidebar-heading">Topics</p>
          <ul className="help-topic-list">
            {topics.map(t => (
              <li key={t.id}>
                <button
                  className={`help-topic-btn${activeId === t.id ? ' help-topic-btn--active' : ''}`}
                  onClick={() => setActiveId(t.id)}
                >
                  {t.title}
                  {t.placeholder && <span className="help-coming-soon">Soon</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <article className="help-content">
          <h1 className="help-content-title">
            {active.title}
            {active.placeholder && <span className="help-coming-soon help-coming-soon--inline">Coming soon</span>}
          </h1>
          <div className="help-content-body">
            {active.content}
          </div>
        </article>
      </main>

      <footer className="landing-footer">
        © {new Date().getFullYear()} Oblivion Solutions Limited ·{' '}
        <Link to="/privacy">Privacy</Link>
      </footer>
    </div>
  );
}
